import React, { useState, useRef, useEffect } from 'react';

interface Option {
  id: string;
  value: string;
  label?: string; // optional display label; falls back to value
}

interface CustomFilterDropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  ariaLabel: string;
  placeholder?: string;
}

const CustomFilterDropdown: React.FC<CustomFilterDropdownProps> = ({
  options,
  value,
  onChange,
  onFocus,
  ariaLabel,
  placeholder = 'All'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen && onFocus) {
      onFocus();
    }
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  const displayValue = value || placeholder;
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="custom-filter-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-filter-button ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
      >
        <span className="custom-filter-text">
          {selectedOption ? (selectedOption.label ?? selectedOption.value) : displayValue}
        </span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="12" 
          height="12" 
          fill="currentColor" 
          className="custom-filter-arrow" 
          viewBox="0 0 16 16"
        >
          <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
        </svg>
      </button>
      
      {isOpen && (
        <div className="custom-filter-menu">
          <div
            className={`custom-filter-item ${!value ? 'selected' : ''}`}
            onClick={() => handleSelect('')}
          >
            {placeholder}
          </div>
          {options.map((option, index) => (
            <div
              key={index}
              className={`custom-filter-item ${value === option.value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label ?? option.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomFilterDropdown;

